import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, Upload } from "lucide-react";
import type { BlogPost } from "@/types/blog";
import { blogAdminService } from "@/services/blogAdminService";
import { toast } from "sonner";

const postSchema = z.object({
  title: z.string().min(1, "Título é obrigatório").max(200),
  slug: z.string().optional(),
  excerpt: z.string().max(160, "Resumo deve ter no máximo 160 caracteres").optional(),
  content: z.string().optional(),
  category: z.string().optional(),
  featured_image: z.string().optional(),
});

type PostFormData = z.infer<typeof postSchema>;

interface PostFormProps {
  post?: BlogPost;
  onSubmit: (data: PostFormData, published: boolean) => Promise<void>;
  onDelete?: () => void;
}

export function PostForm({ post, onSubmit, onDelete }: PostFormProps) {
  const [tags, setTags] = useState<string[]>(post?.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<PostFormData>({
    resolver: zodResolver(postSchema),
    defaultValues: {
      title: post?.title || "",
      slug: post?.slug || "",
      excerpt: post?.excerpt || "",
      content: post?.content || "",
      category: post?.category || "",
      featured_image: post?.featured_image || "",
    },
  });

  const featuredImage = watch("featured_image");

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const url = await blogAdminService.uploadCover(file);
      setValue("featured_image", url);
      toast.success("Imagem enviada com sucesso!");
    } catch (error: any) {
      toast.error(error.message || "Erro ao enviar imagem");
    } finally {
      setUploading(false);
    }
  };

  const handleFormSubmit = async (data: PostFormData, published: boolean) => {
    try {
      setSubmitting(true);
      await onSubmit({ ...data, tags } as any, published);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Informações Básicas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              {...register("title")}
              placeholder="Digite o título do post"
            />
            {errors.title && (
              <p className="text-sm text-destructive mt-1">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="slug">Slug (URL amigável)</Label>
            <Input
              id="slug"
              {...register("slug")}
              placeholder="deixe-vazio-para-gerar-automaticamente"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Deixe vazio para gerar automaticamente a partir do título
            </p>
          </div>

          <div>
            <Label htmlFor="excerpt">Resumo (máx 160 caracteres)</Label>
            <Textarea
              id="excerpt"
              {...register("excerpt")}
              placeholder="Resumo curto para SEO e listagens"
              rows={2}
              maxLength={160}
            />
            {errors.excerpt && (
              <p className="text-sm text-destructive mt-1">{errors.excerpt.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="category">Categoria</Label>
            <Input
              id="category"
              {...register("category")}
              placeholder="Ex: Fotografia Imobiliária"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Conteúdo</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            {...register("content")}
            placeholder="Escreva o conteúdo do post aqui... (Markdown/HTML)"
            rows={15}
            className="font-mono text-sm"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Imagem de Capa</CardTitle>
        </CardHeader>
        <CardContent>
          {featuredImage && (
            <div className="mb-4">
              <img
                src={featuredImage}
                alt="Preview"
                className="max-w-md rounded-lg"
              />
            </div>
          )}
          <div>
            <Label htmlFor="cover-upload" className="cursor-pointer">
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:bg-muted/50 transition-colors">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {uploading ? "Enviando..." : "Clique para fazer upload da capa"}
                </p>
              </div>
            </Label>
            <Input
              id="cover-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
              disabled={uploading}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tags</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-3">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              placeholder="Digite uma tag e pressione Enter"
            />
            <Button type="button" onClick={handleAddTag}>
              Adicionar
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="gap-1">
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-1 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={handleSubmit((data) => handleFormSubmit(data, false))}
            variant="outline"
            disabled={submitting}
          >
            Salvar Rascunho
          </Button>
          <Button
            type="button"
            onClick={handleSubmit((data) => handleFormSubmit(data, true))}
            disabled={submitting}
          >
            {post?.published ? "Atualizar" : "Publicar"}
          </Button>
        </div>
        {post && onDelete && (
          <Button
            type="button"
            variant="destructive"
            onClick={onDelete}
            disabled={submitting}
          >
            Excluir Post
          </Button>
        )}
      </div>
    </form>
  );
}
